<template>
  <form @submit.prevent="" class="flex flex-col gap-5 pt-5 border-t">
    <p class="text-lg">
      {{
        $t("labels.generic", {
          pt: "As opções adicionais servirão apenas para aumentar o engajamento e testar o conhecimento dos participantes, portanto NÃO serão consideradas corretas.",
          en:"The aditional options it's only for increase engagement and test participants' knowledge, therefore it will NOT be considered right"
        })
      }}
    </p>

    <div id="btn-action-add" class="flex justify-end">
      <Button
        v-if="!exceedOptions(5)"
        :label="$t('labels.generic', {pt:'Adicionar opção', en:'Add option'})"
        icon="pi pi-plus"
        class="btn-simple w-fit"
        @click.prevent="addOption"
      />
    </div>

    <div v-for="(input, index) in options" :key="index">
      <div class="flex justify-between items-center gap-3">
        <i
          id="remove-gap-action"
          class="pi pi-minus mt-6 bg-h-warning-700 rounded-full p-1 text-white cursor-pointer hover:scale-90 transition-all mb-5"
          @click.prevent="removeOption(index)"
        />
        <div class="w-full flex flex-col gap-2">
          <p>{{ "Opção adicional " + (index + 1) }}</p>
          <InputText v-model="input.name" :maxLength="200" />
          <Errors
            :errors="
              questionStore.v$Dialog.$dirty
                ? questionStore.v$Dialog.options.$each.$response.$errors[index]
                    ?.name
                : []
            "
            :actualLength="input.name.length"
            :maxLength="200"
          />
        </div>
      </div>
    </div>

    <div id="actions" class="flex justify-end items-center gap-3">
      <Button label="Cancelar" class="btn-outline" @click.prevent="onClose" />
      <Button label="Confirmar" class="btn-primary" @click.prevent="submit" />
    </div>
  </form>
</template>

<script lang="ts">
import { required, maxLength } from "@/locale/locale.config";
import { useQuizQuestionsDragNDropStore } from "@/store/quiz-question-drag";
import { Messages } from "@/utils/messages.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import useVuelidate from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { useToast } from "primevue/usetoast";
import { v4 as uuidv4 } from "uuid";
import { defineComponent, onMounted, onUnmounted, ref, type Ref } from "vue";
import { useI18n } from "vue-i18n";

export class OptionInput {
  constructor(public id: string = uuidv4(), public name: string = "") {}
}

export default defineComponent({
  name: "FormOptions",
  emits: ["onClose"],
  props: {
    isEditing: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const questionStore = useQuizQuestionsDragNDropStore;
    const options: Ref<OptionInput[]> = ref([...questionStore.options]);
    const toast = useToast();
    const form = ref({
      options: options,
    });
    const { t } = useI18n();
    const v$ = useVuelidate(
      {
        options: {
          $each: helpers.forEach({
            name: {
              required: helpers.withMessage(
                t("validations.required"),
                required
              ),
              max: helpers.withMessage(
                t("validations.max", { max: 200 }),
                maxLength(200)
              ),
            },
          }),
        },
      },
      form.value
    );

    function addOption(): void {
      if (exceedOptions(5)) return;
      options.value.push(new OptionInput());
    }

    function removeOption(index): void {
      options.value.splice(index, 1);
    }

    function exceedOptions(limit: number) {
      if (props.isEditing) return options.value.length >= limit;
      else return options.value.length + questionStore.options.length >= limit;
    }
    function submit(): void {
      questionStore.v$Dialog.$touch();
      if (questionStore.v$Dialog.$invalid) return;
      if (exceedOptions(6)) {
        toast.add(ToastFactory.error(Messages.OPTIONS_LIMIT_EXCEEDED));
        return;
      }
      questionStore.setOptions(options.value, props.isEditing);
      onClose();
    }

    function onClose(): void {
      questionStore.v$Dialog.$reset();
      emit("onClose");
    }

    onMounted(() => {
      if (props.isEditing) {
        questionStore.optionsDialog = [...questionStore.options];
        options.value = questionStore.optionsDialog.map(
          (opt) => new OptionInput(opt.id, opt.name)
        );
      } else {
        options.value = new Array(1).fill(null).map(() => new OptionInput());
      }

      questionStore.setVDialog(v$);
      questionStore.setSentenceForm(form.value);
    });
    onUnmounted(() => {
      questionStore.v$Dialog.$reset();
    });

    return {
      options,
      addOption,
      removeOption,
      exceedOptions,
      onClose,
      submit,
      questionStore,
    };
  },
});
</script>
